import {
  jobPauseTypeToBackgroundColor,
  jobPauseTypeToDotColor,
  jobPauseTypeToHoverBackgroundColor,
  jobPauseTypeToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { Job, JobPauseType, JobPendingPauseRequest, JobStatus } from "@/types";

export const jobs = {
  // Split a comma-separated keywords string into trimmed, non-empty keywords.
  parseKeywords: (keywords?: string): string[] => {
    if (!keywords) return [];
    return keywords
      .split(",")
      .map((keyword) => keyword.trim())
      .filter(Boolean);
  },

  // Paused is a derived display state. `guac` keeps status ACTIVE and
  // sets pausedAt, and only that combination renders as paused.
  isDisplayPaused: (status: JobStatus, pausedAt: string | null): boolean => {
    return status === JobStatus.ACTIVE && !!pausedAt;
  },

  // Muted on the dashboard: paused, covered, or still a draft. All three are
  // jobs a staffer isn't meant to be working, so they dim, sink to the bottom,
  // and drop out of the group totals.
  isMutedOnDashboard: (status: JobStatus, pausedAt: string | null): boolean =>
    status === JobStatus.DRAFT || !!pausedAt,

  // A job paused before covered existed carries a null type and reads as Paused.
  displayPauseType: (pauseType: JobPauseType | null): JobPauseType =>
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    pauseType || JobPauseType.PAUSED,

  // Badge text for a paused/covered job.
  pauseLabel: (pauseType: JobPauseType | null): string =>
    jobPauseTypeToLabel[jobs.displayPauseType(pauseType)],

  // Badge dot color for a paused/covered job.
  pauseDotColor: (pauseType: JobPauseType | null): string =>
    jobPauseTypeToDotColor[jobs.displayPauseType(pauseType)],

  // Filled-badge background (and its hover twin) for a paused/covered job.
  pauseBackgroundColor: (pauseType: JobPauseType | null): string =>
    jobPauseTypeToBackgroundColor[jobs.displayPauseType(pauseType)],

  pauseHoverBackgroundColor: (pauseType: JobPauseType | null): string =>
    jobPauseTypeToHoverBackgroundColor[jobs.displayPauseType(pauseType)],

  // Hover text for paused/covered chips: when + why the job was paused.
  pausedTooltip: (
    pausedAt: string | null,
    pauseReason: string | null,
    pauseType: JobPauseType | null,
  ): string | undefined => {
    if (!pausedAt) return undefined;
    const since = `${jobs.pauseLabel(pauseType)} ${dates.formatShortDate(pausedAt)}`;
    return pauseReason ? `${since} — ${pauseReason}` : since;
  },

  // Why the job can't be activated yet, or undefined when it can. Undefined
  // when readiness is absent too — the reads that feed the status dropdown
  // carry it, and guac rejects the activation either way.
  activationBlockedReason: (job: Job): string | undefined => {
    if (!job.activationReadiness || job.status === JobStatus.ACTIVE) {
      return undefined;
    }
    const { hasActiveRequisition, hasRotationOwner } = job.activationReadiness;
    if (!hasActiveRequisition) {
      return "This job needs an open req before it can be activated.";
    }
    if (!hasRotationOwner) {
      return "This job needs an owner on one of its reqs before it can be activated.";
    }
    return undefined;
  },

  // Second line on a pending request item: who asked, when, and their reason.
  pauseRequestSummary: (request: JobPendingPauseRequest): string =>
    `${request.requestedByName} · ${dates.formatShortDate(request.createdAt)} — ${request.pauseReason}`,
};
